/**
 * Field Help — registry + accessor
 *
 * Thin lookup over the per-entity help maps. Each entity's content lives in its
 * own small file (./deals, ./contacts, …) so the data stays modular and easy to
 * maintain — this file only wires them together and exposes `getFieldHelp`.
 *
 * Usage:
 *   const help = getFieldHelp("deals", "revenue");
 *   if (help) { ... render ? popover ... }
 */

import type { EntityFieldHelp, FieldHelp, FieldHelpEntityKey } from "./types";

import { dealFieldHelp } from "./deals";
import { contactFieldHelp } from "./contacts";
import { companyFieldHelp } from "./companies";
import { taskFieldHelp } from "./tasks";
import { ticketFieldHelp } from "./tickets";
import { timeEntryFieldHelp } from "./time-entries";
import { socialMediaPostFieldHelp } from "./social-media-posts";
import { emailCampaignFieldHelp } from "./email-campaigns";
import { recurringTransactionFieldHelp } from "./recurring-transactions";
import { recurringChargeFieldHelp } from "./recurring-charges";

export type { FieldHelp, EntityFieldHelp, FieldHelpEntityKey } from "./types";

const FIELD_HELP_REGISTRY: Record<FieldHelpEntityKey, EntityFieldHelp> = {
  deals: dealFieldHelp,
  contacts: contactFieldHelp,
  companies: companyFieldHelp,
  tasks: taskFieldHelp,
  tickets: ticketFieldHelp,
  time_entries: timeEntryFieldHelp,
  social_media_posts: socialMediaPostFieldHelp,
  email_campaigns: emailCampaignFieldHelp,
  "recurring-transactions": recurringTransactionFieldHelp,
  "recurring-charges": recurringChargeFieldHelp,
};

function isKnownEntity(entity: string): entity is FieldHelpEntityKey {
  return entity in FIELD_HELP_REGISTRY;
}

/**
 * Look up help for a single field. Returns `undefined` when the entity is
 * unknown or the field has no help defined — callers render nothing in that case.
 *
 * `entity` accepts any string (the value surfaces hold) and is narrowed safely.
 */
export function getFieldHelp(entity: string, fieldId: string): FieldHelp | undefined {
  if (!isKnownEntity(entity)) return undefined;
  return FIELD_HELP_REGISTRY[entity][fieldId];
}

/** Whether help exists for a field (cheap check for conditional rendering). */
export function hasFieldHelp(entity: string, fieldId: string): boolean {
  return getFieldHelp(entity, fieldId) !== undefined;
}

/** All help entries for an entity (e.g. for a card-level "explain fields" popover). */
export function getEntityFieldHelp(entity: string): EntityFieldHelp {
  return isKnownEntity(entity) ? FIELD_HELP_REGISTRY[entity] : {};
}
